import { formatDisplayDate } from "./formatDisplayDate";
import { formatRecurringWeeklyDaysDisplay } from "./formatRecurringWeeklyDays";

/**
 * Deal Display Helpers for Frontend
 *
 * Helper functions to display deal information using the cleaned-up schema
 */

/**
 * Get discount display text
 * @param {Object} deal - Deal object
 * @returns {string|null} Display text for discount
 */
export const getDiscountDisplay = (deal) => {
  if (!deal?.discount) return null;

  const { type, value, buy, free, label } = deal.discount;

  switch (type) {
    case "amount":
      return value ? `RM ${value.toFixed(2)}` : null;
    
    case "percentage":
      return value ? `${value}% OFF` : null;
    
    case "bxfy":
      return buy && free ? `Buy ${buy} Free ${free}` : null;
    
    case "label":
      return label || null;
    
    default:
      return null;
  }
};

/**
 * Get short discount badge text
 * @param {Object} deal - Deal object
 * @returns {string|null} Short display text for badge
 */
export const getDiscountBadge = (deal) => {
  if (!deal?.discount) return null;

  const { type, value, buy, free, label } = deal.discount;

  switch (type) {
    case "amount":
      return value ? `RM ${value.toFixed(2)}` : null;
    
    case "percentage":
      return value ? `${value}%` : null;
    
    case "bxfy":
      return buy && free ? `B${buy}F${free}` : null;
    
    case "label":
      // Truncate long labels
      if (!label) return null;
      return label.length > 15 ? `${label.substring(0, 15)}...` : label;
    
    default:
      return null;
  }
};

/**
 * Get validity display text
 * @param {Object} validity - Validity object
 * @returns {string} Display text
 */
export const getValidityDisplay = (validity) => {
  if (!validity?.type) return "Check details";

  switch (validity.type) {
    case "range":
      if (!validity.start || !validity.end) return "Check details";
      const start = formatDisplayDate(validity.start);
      const end = formatDisplayDate(validity.end);
      return `${start} - ${end}`;

    case "specific_dates":
      if (!validity.specific_dates?.length) return "Check details";
      {
        const datesSorted = [...validity.specific_dates].sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime(),
        );
        const formatted = datesSorted
          .map((d) => formatDisplayDate(d))
          .filter(Boolean);
        const preview = formatted.slice(0, 3);
        return formatted.length > 3
          ? `${preview.join(", ")}, ...`
          : preview.join(", ");
      }

    case "recurring_weekly": {
      if (!validity.daysOfWeek?.length) return "Check details";
      const text = formatRecurringWeeklyDaysDisplay(validity.daysOfWeek);
      return text || "Check details";
    }

    case "recurring_weekdays":
      return "Every Monday - Friday";

    case "recurring_weekends":
      return "Every Saturday - Sunday";

    case "recurring_monthly":
      if (!validity.daysOfMonth?.length) return "Check details";
      const dates = validity.daysOfMonth.sort((a, b) => a - b);
      if (dates.length <= 3) {
        return `Monthly: ${dates.map(d => `${d}${getOrdinalSuffix(d)}`).join(", ")}`;
      }
      return `Monthly: ${dates.length} dates`;

    case "not_applicable":
      return "Not specified";

    default:
      return "Check details";
  }
};

/**
 * Whether to show the validity line on compact deal rows (e.g. list rows).
 * Hides non-dates such as "Not specified" / not_applicable.
 */
export const shouldShowDealListValidity = (validity) => {
  if (!validity?.type) return false;
  if (validity.type === "not_applicable") return false;
  const text = getValidityDisplay(validity);
  if (!text || text === "Not specified") return false;
  return true;
};

/**
 * Get ordinal suffix for date
 * @param {number} num - Day of month
 * @returns {string} Ordinal suffix (st, nd, rd, th)
 */
const getOrdinalSuffix = (num) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return "st";
  if (j === 2 && k !== 12) return "nd";
  if (j === 3 && k !== 13) return "rd";
  return "th";
};

/**
 * Check if deal is valid today
 * @param {Object} deal - Deal object
 * @returns {boolean} True if valid today
 */
export const isValidToday = (deal) => {
  const validity = deal?.validity;
  if (!validity?.type) return false;

  const today = new Date();
  const todayDate = today.toDateString();

  switch (validity.type) {
    case "range":
      if (!validity.start || !validity.end) return false;
      return (
        today >= new Date(validity.start) && today <= new Date(validity.end)
      );

    case "specific_dates":
      if (!validity.specific_dates) return false;
      return validity.specific_dates.some(
        (date) => new Date(date).toDateString() === todayDate
      );

    case "recurring_weekly":
      if (!validity.daysOfWeek) return false;
      const dayOfWeek = today.getDay() === 0 ? 7 : today.getDay(); // Convert Sun=0 to Sun=7
      return validity.daysOfWeek.includes(dayOfWeek);

    case "recurring_weekdays":
      const day = today.getDay();
      return day >= 1 && day <= 5; // Mon-Fri

    case "recurring_weekends":
      const weekendDay = today.getDay();
      return weekendDay === 0 || weekendDay === 6; // Sat-Sun

    case "recurring_monthly":
      if (!validity.daysOfMonth) return false;
      const dateOfMonth = today.getDate();
      return validity.daysOfMonth.includes(dateOfMonth);

    case "not_applicable":
      // Matches server list behavior: show in Today / This week feeds
      return true;

    default:
      return false;
  }
};

/**
 * Format area display
 * @param {Array} areas - Array of area strings
 * @returns {string} Formatted area display
 */
export const formatAreaDisplay = (areas) => {
  if (!areas || areas.length === 0) return "";
  if (areas.includes("Islandwide")) return "Islandwide";
  if (areas.length === 1) return areas[0];
  if (areas.length === 2) return areas.join(" & ");
  return `${areas.slice(0, -1).join(", ")} & ${areas[areas.length - 1]}`;
};

/**
 * Format location display
 * @param {Array} locations - Array of location objects
 * @returns {string} Formatted location display
 */
export const formatLocationDisplay = (locations) => {
  if (!locations || locations.length === 0) return "";
  if (locations.length === 1) return locations[0].label;
  if (locations.length === 2) return `${locations[0].label} & ${locations[1].label}`;
  return `${locations[0].label} & ${locations.length - 1} more`;
};

/**
 * Get category display name
 * @param {string} category - Category value
 * @returns {string} Display name
 */
export const getCategoryDisplay = (category) => {
  const displayNames = {
    food: "Food",
    drinks: "Drinks",
    dessert: "Dessert",
    event: "Event",
    bundle: "Bundle",
    promotion: "Promotion",
  };
  return displayNames[category] || category;
};

/**
 * Get time of day display
 * @param {Array} timeOfDay - Array of time periods
 * @returns {string} Display text
 */
export const getTimeOfDayDisplay = (timeOfDay) => {
  if (!timeOfDay || timeOfDay.length === 0) return "All Day";
  if (timeOfDay.includes("all_day")) return "All Day";
  
  const displayNames = {
    breakfast: "Breakfast",
    lunch: "Lunch",
    dinner: "Dinner",
  };

  return timeOfDay.map((t) => displayNames[t] || t).join(", ");
};

/**
 * Get fulfillment display
 * @param {Array} fulfillment - Array of fulfillment methods
 * @returns {string} Display text
 */
export const getFulfillmentDisplay = (fulfillment) => {
  if (!fulfillment || fulfillment.length === 0) return "Dine In";
  
  const displayNames = {
    dine_in: "Dine In",
    delivery: "Delivery",
  };

  return fulfillment.map((f) => displayNames[f] || f).join(" & ");
};

/**
 * Legacy support: Get discount from old deal format
 * This helps during migration period when some deals might still have old format
 * @param {Object} deal - Deal object (old or new format)
 * @returns {string|null} Display text
 */
export const getLegacyDiscountDisplay = (deal) => {
  // Try new format first
  if (deal.discount) {
    return getDiscountDisplay(deal);
  }

  // Fall back to old format (during migration)
  if (deal.discount_price) {
    return `RM ${deal.discount_price.toFixed(2)}`;
  }

  if (deal.discount_label) {
    return deal.discount_label;
  }

  if (deal.bxfy?.buy && deal.bxfy?.free) {
    return `Buy ${deal.bxfy.buy} Free ${deal.bxfy.free}`;
  }

  return null;
};

/**
 * Check if deal has any discount
 * @param {Object} deal - Deal object
 * @returns {boolean} True if deal has discount
 */
export const hasDiscount = (deal) => {
  return !!deal?.discount?.type;
};

/**
 * Check if deal is active
 * @param {Object} deal - Deal object
 * @returns {boolean} True if deal is active
 */
export const isActive = (deal) => {
  return deal?.isActive !== false; // Default to true if not specified
};

const dealDisplayUtils = {
  getDiscountDisplay,
  getDiscountBadge,
  getValidityDisplay,
  isValidToday,
  formatAreaDisplay,
  formatLocationDisplay,
  getCategoryDisplay,
  getTimeOfDayDisplay,
  getFulfillmentDisplay,
  getLegacyDiscountDisplay,
  hasDiscount,
  isActive,
};

export default dealDisplayUtils;

